// front/src/api/TokenService.js

/**
 * TokenService is a small utility responsible for storing and retrieving
 * auth tokens and related user data from browser localStorage.
 */
class TokenService {
  _safeParse(key) {
    try {
      const raw = localStorage.getItem(key);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }

  _safeSetJSON(key, value) {
    try {
      localStorage.setItem(key, JSON.stringify(value));
    } catch {
      // ignore
    }
  }

  _safeSet(key, value) {
    try {
      localStorage.setItem(key, value);
    } catch {
      // ignore
    }
  }

  _safeRemove(key) {
    try {
      localStorage.removeItem(key);
    } catch {
      // ignore
    }
  }

  // ----------------------------------------
  // "user" object helpers (access / refresh)
  // ----------------------------------------

  getLocalRefreshToken() {
    const user = this._safeParse("user");
    return user?.refresh;
  }

  getLocalAccessToken() {
    const user = this._safeParse("user");
    return user?.access;
  }

  updateLocalAccessToken(token) {
    const user = this._safeParse("user") || {};
    user.access = token;
    this._safeSetJSON("user", user);

    if (token) this._safeSet("token", token);
    else this._safeRemove("token");
  }

  getUser() {
    return this._safeParse("user");
  }

  setUser(user) {
    this._safeSetJSON("user", user);

    if (user?.access) this._safeSet("token", user.access);
    else this._safeRemove("token");

    // اگر بک‌اند refresh را داخل user نگه می‌دارد هم sync کن
    if (user?.refresh) this._safeSet("refresh_token", user.refresh);
  }

  removeUser() {
    this._safeRemove("user");
    this._safeRemove("token");
    this._safeRemove("refresh_token");
    this._safeRemove("verify_token");
    this._safeRemove("username");
  }

  // ----------------------------
  // Username
  // ----------------------------

  setUserName(username) {
    this._safeSet("username", username);
  }

  getUserName() {
    try {
      return localStorage.getItem("username");
    } catch {
      return null;
    }
  }

  // ----------------------------
  // Raw access token ("token")
  // ----------------------------

  setAccessToken(token) {
    if (token) this._safeSet("token", token);
    else this._safeRemove("token");

    const user = this._safeParse("user");
    if (user) {
      user.access = token;
      this._safeSetJSON("user", user);
    }
  }

  getAccessToken() {
    try {
      return localStorage.getItem("token");
    } catch {
      return null;
    }
  }

  removeAccessToken() {
    this._safeRemove("token");

    const user = this._safeParse("user");
    if (user && "access" in user) {
      delete user.access;
      this._safeSetJSON("user", user);
    }
  }

  // ----------------------------
  // Refresh token helpers
  // ----------------------------

  setRefreshToken(refresh) {
    if (refresh) this._safeSet("refresh_token", refresh);
    else this._safeRemove("refresh_token");

    const user = this._safeParse("user");
    if (user) {
      user.refresh = refresh;
      this._safeSetJSON("user", user);
    }
  }

  getRefreshToken() {
    // اول کلید استاندارد شما
    const direct = this.getCustomField("refresh_token");
    if (direct) return direct;
    // fallback به داخل user
    return this.getLocalRefreshToken();
  }

  removeRefreshToken() {
    this._safeRemove("refresh_token");

    const user = this._safeParse("user");
    if (user && "refresh" in user) {
      delete user.refresh;
      this._safeSetJSON("user", user);
    }
  }

  // ----------------------------
  // Generic custom fields
  // ----------------------------

  getCustomField(field_name) {
    try {
      return localStorage.getItem(field_name);
    } catch {
      return null;
    }
  }

  setCustomField(field_name, value) {
    this._safeSet(field_name, value);
  }

  removeCustomField(field_name) {
    this._safeRemove(field_name);
  }

  // ----------------------------
  // Standard helpers for interceptors
  // ----------------------------

  /**
   * Clears any auth-related storage. Safe to call on session expiration.
   */
  clear() {
    this.removeUser();
  }
}

const tokenService = new TokenService();
export default tokenService;
